import { AlertCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";

/**
 * Default auto-dismiss duration for FAILURE toasts (error + warning), in ms.
 *
 * The global `<Toaster duration={4000}>` in `App.tsx` is the platform default and
 * governs successes. A failure needs longer than a success: the dispatcher has to
 * READ which order/route broke and why before acting, whereas "saved" needs only a
 * glance. 10s is the single place this is applied — never pass a duration per call
 * site to make one error linger, or the value drifts across 150+ callers.
 *
 * Overridable per call via `ToastOptions.duration`, or defeated entirely with
 * `persistent: true`.
 */
export const ERROR_TOAST_DURATION_MS = 10_000;

interface ToastOptions {
  /** When true, the toast will not auto-dismiss (duration: Infinity) and shows a close button. */
  persistent?: boolean;
  /**
   * Stable toast id. Reusing the same id makes sonner REPLACE the existing toast
   * instead of stacking a new one — used to coalesce bursts of same-typed events
   * into a single, self-dismissing toast (BUG-321).
   *
   * ---------------------------------------------------------------------------
   * ID CONVENTION — a failure toast and a success toast MUST NEVER share an id.
   * ---------------------------------------------------------------------------
   * sonner keys purely on the id: a later `toast.success` with the id of a live
   * error REPLACES it, and the failure disappears with no trace. In a batch build
   * that is how a dispatcher ends up seeing only "Маршруты построены" while the
   * failures that arrived alongside it were silently overwritten
   * (`useNotificationsWebSocket.ts` coalesces on `notification-${notification_type}`,
   * which is outcome-blind).
   *
   * Therefore every coalescing id MUST carry its OUTCOME as a prefix segment:
   *
   *     success:<domain>:<discriminator>     e.g. `success:notification:routes_built`
   *     error:<domain>:<discriminator>       e.g. `error:notification:routes_failed`
   *     warning:<domain>:<discriminator>     e.g. `warning:geocode:partial`
   *
   * Prefer building ids with the `toastId` helper below, which cannot produce a
   * colliding pair by construction. Coalescing WITHIN one outcome is the point and
   * stays intact — 20 failures of the same type still collapse into one toast.
   */
  id?: string | number;
  /** Explicit auto-dismiss duration in ms. Ignored when `persistent` is true. */
  duration?: number;
}

/**
 * Build a coalescing toast id that cannot collide across outcomes.
 *
 * The `outcome` segment is mandatory, so a success and a failure describing the
 * same domain event produce DIFFERENT ids and therefore stack instead of one
 * silently replacing the other. See the `id` convention on `ToastOptions`.
 */
export const toastId = (
  outcome: "success" | "error" | "warning",
  domain: string,
  discriminator: string,
): string => `${outcome}:${domain}:${discriminator}`;

/**
 * Duration/id spreading shared by all three toast flavours, so success, error and
 * warning stay behaviourally symmetric and the `persistent` branch exists once.
 *
 * `defaultDuration` is the flavour's own default (undefined = inherit the global
 * `<Toaster duration>`); an explicit `options.duration` always wins over it.
 */
const resolveOptions = (
  options: ToastOptions | undefined,
  defaultDuration?: number,
) => ({
  ...(options?.id !== undefined ? { id: options.id } : undefined),
  ...(options?.persistent
    ? { duration: Infinity, closeButton: true }
    : options?.duration !== undefined
      ? { duration: options.duration }
      : defaultDuration !== undefined
        ? { duration: defaultDuration }
        : undefined),
});

export const toastSuccess = (
  message: string,
  description?: ReactNode,
  options?: ToastOptions,
) =>
  toast.success(message, {
    icon: <CheckCircle2 size={16} className="text-brand-success" />,
    description,
    ...resolveOptions(options),
  });

export const toastError = (
  message: string,
  description?: ReactNode,
  options?: ToastOptions,
) =>
  toast.error(message, {
    icon: <AlertCircle size={16} className="text-brand-danger" />,
    description,
    ...resolveOptions(options, ERROR_TOAST_DURATION_MS),
  });

export const toastWarning = (
  message: string,
  description?: ReactNode,
  options?: ToastOptions,
) =>
  toast.warning(message, {
    icon: <AlertTriangle size={16} className="text-brand-warning" />,
    description,
    ...resolveOptions(options, ERROR_TOAST_DURATION_MS),
  });
